import React, { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "src/app/components/Button/Button";
import { DropdownPopper } from "src/app/components/DropdownPopper/DropdownPopper";
import {
  DropdownMenu,
  DropdownMenuTitle,
  DropdownMenuSeparator,
} from "src/app/components/Dropdown/Dropdown";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { VerticalDragAndDropList } from "src/app/components/VerticalDragAndDropList/VerticalDragAndDropList";
import {
  toggleColumnVisibility,
  setColumnOrder,
} from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/CreatorSearch/redux/creatorSearchActions";
import { ReactComponent as SettingsIcon } from "src/images/settings-alt.svg";
import CreatorSearchColumnRow from "./CreatorSearchColumnRow";
import "./CreatorSearchColumnsDropdown.scss";

const COLUMN_KEYS_BLACKLIST = ["name"];

export default function CreatorSearchColumnsDropdown() {
  const dispatch = useDispatch();
  const dropdownPopperRef = useRef(null);

  const columns = useSelector((state) => state.creatorSearchReducer.columns);
  const hiddenColumns = useSelector(
    (state) => state.creatorSearchReducer.hiddenColumns,
  );

  const toggleableColumns = columns.filter(
    (column) => !COLUMN_KEYS_BLACKLIST.includes(column.key),
  );

  useEffect(() => {
    const handler = () => {
      dropdownPopperRef.current?.forceVisible?.(false);
    };
    document.addEventListener("close-creator-search-settings", handler);
    return () =>
      document.removeEventListener("close-creator-search-settings", handler);
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = toggleableColumns.findIndex(
      (column) => column.key === active.id,
    );
    const newIndex = toggleableColumns.findIndex(
      (column) => column.key === over.id,
    );
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedColumns = Array.from(toggleableColumns);
    const [movedColumn] = reorderedColumns.splice(oldIndex, 1);
    reorderedColumns.splice(newIndex, 0, movedColumn);

    dispatch(setColumnOrder(reorderedColumns.map((column) => column.key)));
  };

  return (
    // @ts-ignore
    <DropdownPopper
      ref={dropdownPopperRef}
      placement="bottom-start"
      overlay={
        <DropdownMenu isOpen className="creator-search-columns-dropdown">
          <div className="creator-search-columns-dropdown__tab-name">
            <IDHFormattedMessage
              id="ws_creator_search"
              defaultMessage="Creator Search"
            />
          </div>
          <DropdownMenuTitle>
            <IDHFormattedMessage
              id="ws_search_results"
              defaultMessage="Search Results"
            />
          </DropdownMenuTitle>
          <DropdownMenuSeparator dashed />
          <VerticalDragAndDropList.DndContext onDragEnd={handleDragEnd}>
            <VerticalDragAndDropList.SortableContext
              items={toggleableColumns.map((column) => column.key)}
            >
              <ul className="creator-search-columns-dropdown__list">
                {toggleableColumns.map((column) => (
                  <CreatorSearchColumnRow
                    key={column.key}
                    column={column}
                    isHidden={hiddenColumns.includes(column.key)}
                    onToggleVisibility={() =>
                      dispatch(toggleColumnVisibility(column.key))
                    }
                  />
                ))}
              </ul>
            </VerticalDragAndDropList.SortableContext>
          </VerticalDragAndDropList.DndContext>
        </DropdownMenu>
      }
    >
      <Button
        variant="white"
        size="small"
        textWithIcon
        dataQa="creator-search-settings-button"
      >
        <SettingsIcon />
        <IDHFormattedMessage id="ws_settings" defaultMessage="Settings" />
      </Button>
    </DropdownPopper>
  );
}
